import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DeckBimViewer from "./DeckBimViewer/DeckBimViewer";
import type {
  IPixBimCameraState,
  IPixCircle,
  IPixDimensionLine,
  IPixPath,
  IPixPolygon,
  PixBimViewerAction,
} from "./DeckBimViewer/types";
import { invertTransform } from "./DeckBimViewer/transformUtils";
import BoundingBoxViewer3d from "./BoundingBoxViewer3d/BoundingBoxViewer3d";
import type { BoundingBoxViewer3dProps, BoundingBoxViewer3dViewMode } from "./BoundingBoxViewer3d/BoundingBoxViewer3d";
import type { IMcBimItem3d } from "./RoomSectionViews/types";
import { Viewer3d } from "./Viewer3d";
import { getStreetViewRoomLabel, getStreetViewStartPosition } from "./streetViewPosition";
import { ViewerModeButtonBar } from "./ViewerModeButtonBar";
import type { IViewPoint2d, ViewerSyncState } from "./viewerSyncTypes";

type MultiViewerDisplayMode = "ONLY_2D" | "ONLY_3D" | "BOTH_2D_3D";
type Pos2 = [number, number];
type BoundingBoxRenderItem = BoundingBoxViewer3dProps["boundingBoxViewer3dItems"][number];
export type MultiViewerStreetViewPosition = { x: number; y: number };

export type MultiViewerFragFileRef = {
  fileName: string;
  frag: {
    blobName?: string;
    url: string;
    sizeBytes?: number;
  };
  manifest: {
    blobName?: string;
    sizeBytes?: number;
  };
};

export type MultiViewerStreetViewSpatialItem = {
  id: string;
  centerPoint?: number[] | null;
  bbox?: number[][] | null;
  polygon?: number[][] | null;
};

export type MultiViewerStreetViewLoadArgs = {
  itemIds: string[];
  center: Pos2;
  radiusM: number;
};

export type MultiViewerStreetViewItem3d = IMcBimItem3d & {
  _id: unknown;
};

export type MultiViewerStreetViewCacheOptions = {
  enabled?: boolean;
  ttlDays?: number;
};

export type MultiViewerStreetViewOptions = {
  /** Walkable room footprints in original model coordinates, used when clicking Streetview. */
  entryPolygons?: Array<Pick<IPixPolygon, "id" | "points"> & Partial<Pick<IPixPolygon, "text">>>;
  /** Name of the active plan used to resolve room footprints. */
  floorName?: string;
  /** Initial camera Z in world coordinates. */
  initialCameraZ?: number;
  preferredEntryPolygonId?: string | null;
  radiusM?: number;
  refreshMarginM?: number;
  cache?: MultiViewerStreetViewCacheOptions;
  spatialItems?: MultiViewerStreetViewSpatialItem[];
  loadItems3d?: (
    args: MultiViewerStreetViewLoadArgs
  ) => Promise<MultiViewerStreetViewItem3d[]>;
  renderItems3d?: (
    items: MultiViewerStreetViewItem3d[],
    view: { center: Pos2 | null; radiusM: number }
  ) => BoundingBoxRenderItem[];
  onDrop?: (position: MultiViewerStreetViewPosition) => void;
  onReset?: () => void;
  /** Reports whether Streetview is active. */
  onActiveChange?: (active: boolean) => void;
};

const DEFAULT_STREET_VIEW_RADIUS_M = 10;
const DEFAULT_STREET_VIEW_REFRESH_MARGIN_M = 2;
const DEFAULT_STREET_VIEW_CACHE_TTL_DAYS = 14;
const STREET_VIEW_CACHE_DB_NAME = "multiplacer-ui-street-view-cache";
const STREET_VIEW_CACHE_STORE_NAME = "items3d";
const STREET_VIEW_CACHE_VERSION = 1;

type StreetViewCacheRecord = {
  id: string;
  item: MultiViewerStreetViewItem3d;
  lastAccessedAt: number;
  cacheVersion: number;
};

let streetViewCacheDbPromise: Promise<IDBDatabase> | null = null;

const transactionDone = (tx: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });

const openStreetViewCacheDb = (): Promise<IDBDatabase> => {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error("IndexedDB is not available"));
  }

  if (streetViewCacheDbPromise) return streetViewCacheDbPromise;

  streetViewCacheDbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(STREET_VIEW_CACHE_DB_NAME, 1);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STREET_VIEW_CACHE_STORE_NAME)) {
        db.createObjectStore(STREET_VIEW_CACHE_STORE_NAME, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return streetViewCacheDbPromise;
};

const getItemCacheId = (item: MultiViewerStreetViewItem3d): string | null => {
  const id = item?._id;
  if (id == null) return null;
  return String(id);
};

const readStreetViewItemsFromCache = async (
  ids: string[],
  ttlMs: number
): Promise<Map<string, MultiViewerStreetViewItem3d>> => {
  const db = await openStreetViewCacheDb();
  const tx = db.transaction(STREET_VIEW_CACHE_STORE_NAME, "readwrite");
  const done = transactionDone(tx);
  const store = tx.objectStore(STREET_VIEW_CACHE_STORE_NAME);
  const now = Date.now();
  const minLastAccessedAt = now - ttlMs;
  const out = new Map<string, MultiViewerStreetViewItem3d>();

  await Promise.all(
    ids.map(
      (id) =>
        new Promise<void>((resolve, reject) => {
          const request = store.get(id);
          request.onerror = () => reject(request.error);
          request.onsuccess = () => {
            const record = request.result as StreetViewCacheRecord | undefined;
            if (
              record &&
              record.cacheVersion === STREET_VIEW_CACHE_VERSION &&
              record.lastAccessedAt >= minLastAccessedAt
            ) {
              out.set(id, record.item);
              store.put({ ...record, lastAccessedAt: now });
            } else if (record) {
              store.delete(id);
            }
            resolve();
          };
        })
    )
  );

  await done;
  return out;
};

const writeStreetViewItemsToCache = async (
  items: MultiViewerStreetViewItem3d[]
): Promise<void> => {
  if (items.length === 0) return;

  const db = await openStreetViewCacheDb();
  const tx = db.transaction(STREET_VIEW_CACHE_STORE_NAME, "readwrite");
  const done = transactionDone(tx);
  const store = tx.objectStore(STREET_VIEW_CACHE_STORE_NAME);
  const now = Date.now();

  for (const item of items) {
    const id = getItemCacheId(item);
    if (!id) continue;

    store.put({
      id,
      item,
      lastAccessedAt: now,
      cacheVersion: STREET_VIEW_CACHE_VERSION,
    } satisfies StreetViewCacheRecord);
  }

  await done;
};

const pruneExpiredStreetViewCache = async (ttlMs: number): Promise<void> => {
  const db = await openStreetViewCacheDb();
  const tx = db.transaction(STREET_VIEW_CACHE_STORE_NAME, "readwrite");
  const done = transactionDone(tx);
  const store = tx.objectStore(STREET_VIEW_CACHE_STORE_NAME);
  const minLastAccessedAt = Date.now() - ttlMs;

  await new Promise<void>((resolve, reject) => {
    const request = store.openCursor();
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) {
        resolve();
        return;
      }

      const record = cursor.value as StreetViewCacheRecord;
      if (
        record.cacheVersion !== STREET_VIEW_CACHE_VERSION ||
        record.lastAccessedAt < minLastAccessedAt
      ) {
        cursor.delete();
      }
      cursor.continue();
    };
  });

  await done;
};

const squaredDistance = (a: Pos2, b: Pos2): number => {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  return dx * dx + dy * dy;
};

const toPos2 = (value: number[] | null | undefined): Pos2 | null => {
  const x = value?.[0];
  const y = value?.[1];
  return typeof x === "number" && typeof y === "number" && Number.isFinite(x) && Number.isFinite(y)
    ? [x, y]
    : null;
};

const pointToSegmentSquaredDistance = (point: Pos2, start: Pos2, end: Pos2): number => {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const segmentLengthSq = dx * dx + dy * dy;

  if (segmentLengthSq === 0) return squaredDistance(point, start);

  const t = Math.max(
    0,
    Math.min(1, ((point[0] - start[0]) * dx + (point[1] - start[1]) * dy) / segmentLengthSq)
  );
  const projection: Pos2 = [start[0] + t * dx, start[1] + t * dy];

  return squaredDistance(point, projection);
};

const geometryIntersectsRadius = (
  geometry: number[][] | null | undefined,
  center: Pos2,
  radiusSq: number
): boolean => {
  if (!geometry?.length) return false;

  const points = geometry.map(toPos2).filter((point): point is Pos2 => point != null);
  if (points.some((point) => squaredDistance(point, center) <= radiusSq)) return true;

  if (points.some((point, index) => {
    const next = points[(index + 1) % points.length];
    return next != null && pointToSegmentSquaredDistance(center, point, next) <= radiusSq;
  })) return true;

  // The circle can lie wholly inside a footprint without touching its edges.
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const [xi, yi] = points[i];
    const [xj, yj] = points[j];
    if (
      (yi > center[1]) !== (yj > center[1]) &&
      center[0] < ((xj - xi) * (center[1] - yi)) / (yj - yi) + xi
    ) {
      inside = !inside;
    }
  }
  return inside;
};

const getSpatialItemIdsWithinRadius = (
  items: MultiViewerStreetViewSpatialItem[],
  center: Pos2,
  radiusM: number
): string[] => {
  const radiusSq = radiusM * radiusM;

  return items
    .filter((item) => {
      const centerPoint = toPos2(item.centerPoint);
      if (centerPoint && squaredDistance(centerPoint, center) <= radiusSq) return true;

      return (
        geometryIntersectsRadius(item.bbox, center, radiusSq) ||
        geometryIntersectsRadius(item.polygon, center, radiusSq)
      );
    })
    .map((item) => item.id);
};

export interface MultiViewerProps {
  twoDOnly?: boolean;
  twoD: {
    polygons: IPixPolygon[];
    paths?: IPixPath[];
    circles?: IPixCircle[];
    dimensionLines?: IPixDimensionLine[];
    angle?: number;
    action?: PixBimViewerAction;
    zoomExtentKey?: string | number | null;
    goToPolygon?: string;
    highlightPolygon?: string | null;
    transformPivot?: { x: number; y: number } | null;
    useLeftClickToPan?: boolean;
    flip2dCoordinate?: boolean;
    initialCamera?: {
      zoom: number;
      centerWorld: { x: number; y: number };
    };
    onCameraChange?: (camera: IPixBimCameraState) => void;
    onMoveStart?: (id: string) => void;
    onRightMenuClick?: (action: string, polygon: IPixPolygon) => void;
    onClick?: (id: string | undefined, coordinates: [number, number]) => void;
    onDragAndDrop?: (id: string, points: number[][]) => void;
    onDragSelected?: (ids: string[]) => void;
    onDrawPath?: (start: [number, number], end: [number, number]) => void;
  };
  threeD: {
    fragFiles: MultiViewerFragFileRef[];
    isLoadingFragFiles?: boolean;
    isFetchingFragFiles?: boolean;
    fragFilesError?: unknown;
    visibleMcbimFileNames: string[];
    clipZMm?: { bottom: number; top: number } | null;
    onObjectClick?: (guid: string) => void;
  };
  boundingBox3d?: {
    boundingBoxViewer3dItems?: BoundingBoxViewer3dProps["boundingBoxViewer3dItems"];
    onItemClick?: BoundingBoxViewer3dProps["onItemClick"];
  };
  streetView?: MultiViewerStreetViewOptions;
  /** @deprecated Use streetView.onDrop instead. */
  onStreetViewDrop?: (position: { x: number; y: number }) => void;
  /** @deprecated Use streetView.onReset instead. */
  onStreetViewReset?: () => void;
}

export const MultiViewer: React.FC<MultiViewerProps> = ({
  twoDOnly = false,
  twoD,
  threeD,
  boundingBox3d,
  streetView,
  onStreetViewDrop,
  onStreetViewReset,
}) => {
  const {
    polygons,
    paths = [],
    circles = [],
    dimensionLines = [],
    angle = 180,
    action = "normal",
    zoomExtentKey,
    goToPolygon,
    highlightPolygon,
    transformPivot,
    useLeftClickToPan = false,
    flip2dCoordinate = true,
    initialCamera,
    onCameraChange,
    onMoveStart,
    onRightMenuClick,
    onClick,
    onDragAndDrop,
    onDragSelected,
    onDrawPath,
  } = twoD;
  const {
    fragFiles,
    isLoadingFragFiles,
    isFetchingFragFiles,
    fragFilesError,
    visibleMcbimFileNames,
    clipZMm,
    onObjectClick,
  } = threeD;
  const fallbackBoundingBoxViewer3dItems = boundingBox3d?.boundingBoxViewer3dItems ?? [];
  const streetViewRadiusM = streetView?.radiusM ?? DEFAULT_STREET_VIEW_RADIUS_M;
  const streetViewRefreshMarginM =
    streetView?.refreshMarginM ?? DEFAULT_STREET_VIEW_REFRESH_MARGIN_M;
  const streetViewCacheEnabled = streetView?.cache?.enabled ?? true;
  const streetViewCacheTtlMs =
    (streetView?.cache?.ttlDays ?? DEFAULT_STREET_VIEW_CACHE_TTL_DAYS) * 24 * 60 * 60 * 1000;
  const onStreetViewDropCallback = streetView?.onDrop ?? onStreetViewDrop;
  const onStreetViewResetCallback = streetView?.onReset ?? onStreetViewReset;
  const [displayMode, setDisplayMode] = useState<MultiViewerDisplayMode>("ONLY_2D");
  const [hideIfcCovering, setHideIfcCovering] = useState(true);
  const [clipperActive, setClipperActive] = useState(false);
  const [deleteAllClipPlanesSignal, setDeleteAllClipPlanesSignal] = useState(0);
  const [gridVisible, setGridVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const deckContainerRef = useRef<HTMLDivElement>(null);
  // Street view marker in original model coordinates
  const [streetViewModelPos, setStreetViewModelPos] = useState<{ x: number; y: number } | null>(null);
  const [streetViewCameraModelPos, setStreetViewCameraModelPos] = useState<{ x: number; y: number } | null>(null);
  const [streetViewQueryCenter, setStreetViewQueryCenter] = useState<Pos2 | null>(null);
  const [loadedStreetViewItems3d, setLoadedStreetViewItems3d] = useState<
    MultiViewerStreetViewItem3d[]
  >([]);
  const [streetViewCameraDirection, setStreetViewCameraDirection] = useState<[number, number, number] | null>(null);
  const [streetView3dMode, setStreetView3dMode] = useState<BoundingBoxViewer3dViewMode>("walk");
  const streetViewQueryCenterRef = useRef<Pos2 | null>(null);
  const streetViewLoadSeqRef = useRef(0);
  const streetViewInitialCameraPosition = useMemo<[number, number, number] | null>(
    () => streetViewCameraModelPos ? [streetViewCameraModelPos.x, streetViewCameraModelPos.y, 0] : null,
    [streetViewCameraModelPos]
  );

  const streetViewItemIds = useMemo(
    () =>
      streetViewQueryCenter
        ? getSpatialItemIdsWithinRadius(
            streetView?.spatialItems ?? [],
            streetViewQueryCenter,
            streetViewRadiusM
          )
        : [],
    [streetView?.spatialItems, streetViewQueryCenter, streetViewRadiusM]
  );

  const streetViewItemIdsKey = useMemo(() => streetViewItemIds.join("|"), [streetViewItemIds]);

  const boundingBoxViewer3dItems = useMemo(
    () => streetView?.renderItems3d
      ? streetView.renderItems3d(loadedStreetViewItems3d, {
          center: streetViewQueryCenter,
          radiusM: streetViewRadiusM,
        })
      : fallbackBoundingBoxViewer3dItems,
    [fallbackBoundingBoxViewer3dItems, loadedStreetViewItems3d,
      streetView?.renderItems3d, streetViewQueryCenter, streetViewRadiusM]
  );

  const maybeRefreshStreetViewQuery = useCallback(
    (modelPos: { x: number; y: number }) => {
      const nextCenter: Pos2 = [modelPos.x, modelPos.y];
      const currentCenter = streetViewQueryCenterRef.current;
      const refreshDistanceM = streetViewRadiusM - streetViewRefreshMarginM;

      if (
        !currentCenter ||
        Math.sqrt(squaredDistance(nextCenter, currentCenter)) >= refreshDistanceM
      ) {
        streetViewQueryCenterRef.current = nextCenter;
        setStreetViewQueryCenter(nextCenter);
      }
    },
    [streetViewRadiusM, streetViewRefreshMarginM]
  );

  useEffect(() => {
    streetViewQueryCenterRef.current = streetViewQueryCenter;
  }, [streetViewQueryCenter]);

  useEffect(() => {
    const loadItems = streetView?.loadItems3d;
    if (!loadItems || !streetViewQueryCenter) return;

    const seq = ++streetViewLoadSeqRef.current;
    if (streetViewItemIds.length === 0) {
      setLoadedStreetViewItems3d([]);
      return;
    }

    void (async () => {
      const cachedById = streetViewCacheEnabled
        ? await readStreetViewItemsFromCache(streetViewItemIds, streetViewCacheTtlMs).catch(
            () => new Map<string, MultiViewerStreetViewItem3d>()
          )
        : new Map<string, MultiViewerStreetViewItem3d>();

      const cachedItems = streetViewItemIds
        .map((id) => cachedById.get(id))
        .filter((item): item is MultiViewerStreetViewItem3d => item != null);
      const missingIds = streetViewItemIds.filter((id) => !cachedById.has(id));

      if (streetViewLoadSeqRef.current !== seq) return;
      setLoadedStreetViewItems3d(cachedItems);

      if (missingIds.length === 0) return;

      const fetchedItems = await loadItems({
        itemIds: missingIds,
        center: streetViewQueryCenter,
        radiusM: streetViewRadiusM,
      });

      if (streetViewCacheEnabled) {
        await writeStreetViewItemsToCache(fetchedItems).catch(() => undefined);
      }

      if (streetViewLoadSeqRef.current !== seq) return;

      const mergedById = new Map<string, MultiViewerStreetViewItem3d>();
      for (const item of cachedItems) {
        const id = getItemCacheId(item);
        if (id) mergedById.set(id, item);
      }
      for (const item of fetchedItems) {
        const id = getItemCacheId(item);
        if (id) mergedById.set(id, item);
      }

      const mergedItems = streetViewItemIds
          .map((id) => mergedById.get(id))
          .filter((item): item is MultiViewerStreetViewItem3d => item != null);

      setLoadedStreetViewItems3d(mergedItems);
    })().catch(() => {
      if (streetViewLoadSeqRef.current === seq) {
        setLoadedStreetViewItems3d([]);
      }
    });
  }, [
    streetView?.loadItems3d,
    streetViewCacheEnabled,
    streetViewCacheTtlMs,
    streetViewItemIdsKey,
    streetViewQueryCenter,
    streetViewRadiusM,
  ]);

  useEffect(() => {
    if (!streetViewCacheEnabled) return;
    void pruneExpiredStreetViewCache(streetViewCacheTtlMs).catch(() => undefined);
  }, [streetViewCacheEnabled, streetViewCacheTtlMs]);

  // Called by DeckBimViewer when street view is initially placed or dragged.
  // This is an external move, so the 3D camera should follow it.
  const handleStreetViewModelChange = useCallback(
    (modelPos: { x: number; y: number }) => {
      setStreetViewModelPos(modelPos);
      setStreetViewCameraModelPos(modelPos);
      maybeRefreshStreetViewQuery(modelPos);
      onStreetViewDropCallback?.(modelPos);
    },
    [maybeRefreshStreetViewQuery, onStreetViewDropCallback]
  );

  const handleStreetViewDirectionChange = useCallback(
    (direction: { x: number; y: number }) => {
      setStreetViewCameraDirection([direction.x, direction.y, 0]);
    },
    []
  );

  const handleBboxCameraPositionChange = useCallback(
    (position: [number, number, number]) => {
      const modelPos = { x: position[0], y: position[1] };
      setStreetViewModelPos((current) => {
        if (!current) return current;
        return Math.abs(current.x - modelPos.x) < 1e-3 &&
          Math.abs(current.y - modelPos.y) < 1e-3
          ? current
          : modelPos;
      });
      maybeRefreshStreetViewQuery(modelPos);
      onStreetViewDropCallback?.(modelPos);
    },
    [maybeRefreshStreetViewQuery, onStreetViewDropCallback]
  );

  const handleBboxCameraDirectionChange = useCallback(
    (direction: [number, number, number]) => {
      setStreetViewCameraDirection((current) => {
        if (
          current &&
          Math.abs(current[0] - direction[0]) < 1e-4 &&
          Math.abs(current[1] - direction[1]) < 1e-4 &&
          Math.abs(current[2] - direction[2]) < 1e-4
        ) {
          return current;
        }
        return direction;
      });
    },
    []
  );

  // Convert screen drop to local coords for DeckBimViewer
  const [streetViewDropLocal, setStreetViewDropLocal] = useState<{ x: number; y: number } | null>(null);
  const handleStreetViewDropFromToolbar = useCallback(
    (pos: { x: number; y: number }) => {
      if (!deckContainerRef.current) return;
      const deckRect = deckContainerRef.current.getBoundingClientRect();
      if (
        pos.x >= deckRect.left && pos.x <= deckRect.right &&
        pos.y >= deckRect.top && pos.y <= deckRect.bottom
      ) {
        setStreetViewDropLocal({ x: pos.x - deckRect.left, y: pos.y - deckRect.top });
      }
    },
    []
  );
  const effectiveDisplayMode: MultiViewerDisplayMode = twoDOnly ? "ONLY_2D" : displayMode;
  const show2d = effectiveDisplayMode === "ONLY_2D" || effectiveDisplayMode === "BOTH_2D_3D";
  const show3d = effectiveDisplayMode === "ONLY_3D" || effectiveDisplayMode === "BOTH_2D_3D";
  const streetViewRoomLabel = getStreetViewRoomLabel(streetView?.entryPolygons, streetViewModelPos);
  const showBoundingBoxViewer3d = streetViewModelPos !== null;
  useEffect(() => {
    streetView?.onActiveChange?.(showBoundingBoxViewer3d);
  }, [showBoundingBoxViewer3d, streetView?.onActiveChange]);
  const showDeckViewer = show2d || showBoundingBoxViewer3d;
  const showStandard3d = show3d && !showBoundingBoxViewer3d;
  const split = showDeckViewer && (showStandard3d || showBoundingBoxViewer3d);

  // IFC model center in original IFC coordinates (un-flip from transformPivot)
  const ifcModelCenter = useMemo(
    () => transformPivot ? { x: transformPivot.y, y: transformPivot.x } : null,
    [transformPivot]
  );

  const [syncState, setSyncState] = useState<ViewerSyncState>({
    syncViewports: false,
    viewPoint2d: null,
    cameraDirection: [0, 0, -1],
    distance3d: 50,
    zoom2d: null,
  });
  const last3dCameraRef = useRef("");
  const last3dCameraPublishedAtRef = useRef(0);
  const lastViewPoint2dRef = useRef("");
  const lastViewPoint2dPublishedAtRef = useRef(0);

  const angleRef = useRef(angle);
  const flip2dCoordinateRef = useRef(flip2dCoordinate);
  const transformPivotRef = useRef(transformPivot);
  useEffect(() => {
    angleRef.current = angle;
  }, [angle]);
  useEffect(() => {
    flip2dCoordinateRef.current = flip2dCoordinate;
  }, [flip2dCoordinate]);
  useEffect(() => {
    transformPivotRef.current = transformPivot;
  }, [transformPivot]);
  const [applyInitialCamera, setApplyInitialCamera] = useState(
    Boolean(initialCamera),
  );

  useEffect(() => {
    if (!initialCamera) {
      setApplyInitialCamera(false);
      return;
    }
    setApplyInitialCamera(true);
  }, [initialCamera]);

  useEffect(() => {
    if (!applyInitialCamera) return;
    if (!polygons.length) return;

    const id = window.setTimeout(() => setApplyInitialCamera(false), 1000);
    return () => window.clearTimeout(id);
  }, [applyInitialCamera, polygons.length]);

  const handleViewPoint2dChange = useCallback((camera: IPixBimCameraState) => {
    if (camera.zoom == null || !camera.centerWorld) return;
    onCameraChange?.(camera);

    const pivot = transformPivotRef.current;
    const angleDeg = angleRef.current;
    const shouldFlip2dCoordinate = flip2dCoordinateRef.current;
    const [ifcX, ifcY] = pivot
      ? invertTransform(
          [camera.centerWorld.x, camera.centerWorld.y],
          [pivot.x, pivot.y],
          angleDeg,
          shouldFlip2dCoordinate
        )
      : [camera.centerWorld.x, camera.centerWorld.y];

    const vpKey = `${ifcX.toFixed(2)},${ifcY.toFixed(2)},${camera.zoom.toFixed(2)}`;
    const now = performance.now();
    if (
      lastViewPoint2dRef.current !== vpKey &&
      now - lastViewPoint2dPublishedAtRef.current > 100
    ) {
      lastViewPoint2dRef.current = vpKey;
      lastViewPoint2dPublishedAtRef.current = now;
      const viewPoint2d: IViewPoint2d = { x: ifcX, y: ifcY };
      setSyncState((prev) => ({ ...prev, viewPoint2d, zoom2d: camera.zoom ?? null }));
    }
  }, [onCameraChange]);

  const handle3dCameraChange = useCallback(
    (cameraDirection: [number, number, number], distance3d: number) => {
      const key = `${cameraDirection.map((value) => value.toFixed(3)).join(",")}:${distance3d.toFixed(2)}`;
      const now = performance.now();
      if (last3dCameraRef.current === key || now - last3dCameraPublishedAtRef.current < 100) {
        return;
      }
      last3dCameraRef.current = key;
      last3dCameraPublishedAtRef.current = now;
      setSyncState((prev) => ({ ...prev, cameraDirection, distance3d }));
    },
    []
  );

  const [syncTo3DPoint, setSyncTo3DPoint] =
    useState<IViewPoint2d | null>(null);

  // Clear after one frame so the 3D camera flies there then releases
  useEffect(() => {
    if (!syncTo3DPoint) return;
    const id = window.setTimeout(() => setSyncTo3DPoint(null), 100);
    return () => window.clearTimeout(id);
  }, [syncTo3DPoint]);

  const handleSyncViewTo3D = () => {
    setSyncTo3DPoint(syncState.viewPoint2d);
  };

  const streetViewStartPosition = useMemo(() => getStreetViewStartPosition(
    streetView?.entryPolygons ?? polygons,
    streetView?.preferredEntryPolygonId ?? highlightPolygon ?? goToPolygon,
    syncState.viewPoint2d,
  ), [streetView?.entryPolygons, streetView?.preferredEntryPolygonId, polygons, highlightPolygon, goToPolygon, syncState.viewPoint2d]);

  return (
    <div
      ref={containerRef}
      style={{
        height: "100%",
        width: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {!twoDOnly && (
        <ViewerModeButtonBar
          displayMode={displayMode}
          onDisplayModeChange={setDisplayMode}
          syncViewports={syncState.syncViewports}
          onSyncViewTo3D={handleSyncViewTo3D}
          onSyncViewportsChange={(syncViewports) =>
            setSyncState((prev) => ({ ...prev, syncViewports }))
          }
          showIfcCoveringToggle={showStandard3d}
          hideIfcCovering={hideIfcCovering}
          onHideIfcCoveringChange={setHideIfcCovering}
          showClipperToggle={showStandard3d}
          clipperActive={clipperActive}
          onClipperChange={setClipperActive}
          onDeleteAllClipPlanes={() => setDeleteAllClipPlanesSignal((n) => n + 1)}
          showGridToggle={show3d}
          gridVisible={gridVisible}
          onGridVisibleChange={setGridVisible}
          showStreetViewButton={Boolean(
            (streetView?.loadItems3d && streetView.renderItems3d) ||
            boundingBox3d?.boundingBoxViewer3dItems
          )}
          onStreetViewDrop={handleStreetViewDropFromToolbar}
          streetViewCanEnter={streetViewStartPosition !== null}
          onEnterStreetView={() => {
            if (!streetViewStartPosition) return;
            setStreetView3dMode("walk");
            setStreetViewDropLocal(null);
            handleStreetViewModelChange(streetViewStartPosition);
          }}
          streetViewActive={!!streetViewModelPos}    
          streetView3dMode={streetView3dMode}
          onStreetView3dModeChange={setStreetView3dMode}
          onResetStreetView={() => {
            setStreetViewDropLocal(null);
            setStreetViewModelPos(null);
            setStreetViewCameraModelPos(null);
            streetViewQueryCenterRef.current = null;
            setStreetViewQueryCenter(null);
            setLoadedStreetViewItems3d([]);
            onStreetViewResetCallback?.();
          }} 
        />
      )}
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: split ? "row" : "column",
          gap: split ? 1 : 0,
          background: split ? "#ced4da" : undefined,
        }}
      >
        {showDeckViewer && (
          <div
            ref={deckContainerRef}
            style={{
              height: "100%",
              width: split ? "50%" : "100%",
              minWidth: 0,
              position: "relative",
            }}
          >
            <DeckBimViewer
              useLeftClickToPan={useLeftClickToPan}
              flip2dCoordinate={flip2dCoordinate}
              angle={angle}
              //selectedItemFromBboxViewer={selectedItem}
              polygons={polygons}
              circles={circles}
              paths={paths}
              dimensionLines={dimensionLines}
              action={action}
              zoomExtentKey={zoomExtentKey}
              goToPolygon={goToPolygon}
              highlightPolygon={highlightPolygon}
              transformPivot={transformPivot}
              onMoveStart={onMoveStart}
              onRightMenuClick={onRightMenuClick}
              onClick={onClick}
              onDragAndDrop={onDragAndDrop}
              onDragSelected={onDragSelected}
              onDrawPath={onDrawPath}
              {...(applyInitialCamera && initialCamera ? initialCamera : {})}
              onCameraChange={handleViewPoint2dChange}
              streetViewMarkerModel={streetViewModelPos}
              followStreetView={showBoundingBoxViewer3d && streetView3dMode === "walk"}
              streetViewCameraDirection={streetViewCameraDirection}
              onStreetViewMarkerDrag={handleStreetViewModelChange}
              onStreetViewCameraDirectionChange={setStreetViewCameraDirection}
              streetViewDropScreenPos={streetViewDropLocal}
            />
          </div>
        )}
        {showBoundingBoxViewer3d && streetViewInitialCameraPosition && (
          <div
            style={{
              height: "100%",
              width: "50%",
              minWidth: 0,
            }}
          >
            <BoundingBoxViewer3d
              boundingBoxViewer3dItems={boundingBoxViewer3dItems}
              initialCameraPosition={streetViewInitialCameraPosition}
              viewMode={streetView3dMode}
              locationLabel={streetView?.entryPolygons ? streetViewRoomLabel : undefined}
              floorName={streetView?.floorName}
              initialCameraZ={streetView?.initialCameraZ}
              onCameraPositionChange={handleBboxCameraPositionChange}
              onCameraDirectionChange={handleBboxCameraDirectionChange}
              externalLookDirection={streetViewCameraDirection}
              onItemClick={boundingBox3d?.onItemClick}
            />
          </div>
        )}
        {showStandard3d && (
          <div
            style={{
              height: "100%",
              width: split ? "50%" : "100%",
              minWidth: 0,
            }}
          >
            <Viewer3d
              fragFiles={fragFiles}
              isLoadingFragFiles={isLoadingFragFiles}
              isFetchingFragFiles={isFetchingFragFiles}
              fragFilesError={fragFilesError}
              visibleMcbimFileNames={visibleMcbimFileNames}
              clipZMm={clipZMm}
              hideIfcCovering={hideIfcCovering}
              syncState={syncState}
              syncTo3DPoint={syncTo3DPoint}
              ifcModelCenter={ifcModelCenter}
              angleDeg={angle}
              onCameraChange={handle3dCameraChange}
              clipperActive={clipperActive}
              onClipperActiveChange={setClipperActive}
              deleteAllClipPlanesSignal={deleteAllClipPlanesSignal}
              gridVisible={gridVisible}
              onObjectClick={onObjectClick}
            />
          </div>
        )}
      </div>
    </div>
  );
};
